(() => {
  if (window.AmetistikissaCartBenefitsLoaded) return;
  window.AmetistikissaCartBenefitsLoaded = true;

  const GIFT_VARIANT_ID = 56666961740100;

  const hostname = window.location.hostname.toLowerCase();

  const currentLocale = (
    document.documentElement.lang ||
    window.Shopify?.locale ||
    ''
  ).toLowerCase();

  const isEuStore = hostname.endsWith('.eu');

  /*
   * .EU: rajat tulevat keskitetysti window.ACBenefits.current-objektista.
   * .FI: nykyiset 25 € lahja / 60 € ilmainen toimitus.
   */
  const FI_THRESHOLDS = {
    gift: 2500,
    halfShipping: null,
    shipping: 6000,
  };

  const cartRoot = window.Shopify?.routes?.root || '/';

  function getLanguage() {
    return (
      document.documentElement.lang ||
      window.Shopify?.locale ||
      'en'
    ).toLowerCase();
  }

  function fillTemplate(template, values = {}) {
    return Object.entries(values).reduce(
      (output, [name, value]) =>
        output.replaceAll(`__${name}__`, String(value)),
      String(template || '')
    );
  }

  function getHalfShippingFallbacks() {
    const language = getLanguage().split('-')[0];

    const copies = {
      en: {
        remaining: (amount) =>
          `Add <strong>${amount}</strong> more for 50% off shipping.`,
        unlocked: '50% off shipping unlocked!',
        popupRemaining: (amount) =>
          `Add <strong>${amount}</strong> more for 50% off shipping.`,
        popupUnlocked: (amount) =>
          `50% off shipping unlocked! Add <strong>${amount}</strong> more for free shipping.`,
      },

      de: {
        remaining: (amount) =>
          `Noch <strong>${amount}</strong> bis zu 50 % Rabatt auf den Versand.`,
        unlocked: '50 % Versandrabatt freigeschaltet!',
        popupRemaining: (amount) =>
          `Noch <strong>${amount}</strong> bis zu 50 % Rabatt auf den Versand.`,
        popupUnlocked: (amount) =>
          `50 % Versandrabatt freigeschaltet! Noch <strong>${amount}</strong> bis zum kostenlosen Versand.`,
      },

      fr: {
        remaining: (amount) =>
          `Ajoutez encore <strong>${amount}</strong> pour profiter de -50 % sur la livraison.`,
        unlocked: 'Livraison à -50 % débloquée !',
        popupRemaining: (amount) =>
          `Ajoutez encore <strong>${amount}</strong> pour profiter de -50 % sur la livraison.`,
        popupUnlocked: (amount) =>
          `Livraison à -50 % débloquée ! Ajoutez encore <strong>${amount}</strong> pour la livraison gratuite.`,
      },

      it: {
        remaining: (amount) =>
          `Aggiungi ancora <strong>${amount}</strong> per ottenere il 50% di sconto sulla spedizione.`,
        unlocked: 'Spedizione scontata del 50% sbloccata!',
        popupRemaining: (amount) =>
          `Aggiungi ancora <strong>${amount}</strong> per ottenere il 50% di sconto sulla spedizione.`,
        popupUnlocked: (amount) =>
          `Spedizione scontata del 50% sbloccata! Aggiungi ancora <strong>${amount}</strong> per la spedizione gratuita.`,
      },

      pl: {
        remaining: (amount) =>
          `Dodaj jeszcze <strong>${amount}</strong>, aby otrzymać 50% zniżki na dostawę.`,
        unlocked: '50% zniżki na dostawę odblokowane!',
        popupRemaining: (amount) =>
          `Dodaj jeszcze <strong>${amount}</strong>, aby otrzymać 50% zniżki na dostawę.`,
        popupUnlocked: (amount) =>
          `50% zniżki na dostawę odblokowane! Dodaj jeszcze <strong>${amount}</strong>, aby otrzymać darmową dostawę.`,
      },

      sv: {
        remaining: (amount) =>
          `Lägg till <strong>${amount}</strong> för 50 % rabatt på frakten.`,
        unlocked: '50 % rabatt på frakten upplåst!',
        popupRemaining: (amount) =>
          `Lägg till <strong>${amount}</strong> för 50 % rabatt på frakten.`,
        popupUnlocked: (amount) =>
          `50 % rabatt på frakten upplåst! Lägg till <strong>${amount}</strong> för fri frakt.`,
      },
    };

    return copies[language] || copies.en;
  }

  /*
   * EU:ssa lisätään puolitetun toimituksen milestone automaattisesti
   * kopioimalla olemassa oleva toimitus-milestone.
   *
   * Näin nykyistä Liquid/HTML-rakennetta ei tarvitse
   * muuttaa tätä varten.
   */
  function ensureHalfShippingMilestone(
    container,
    floating = false
  ) {
    if (!isEuStore || !container) return null;

    const halfSelector = floating
      ? '[data-floating-half-shipping-milestone]'
      : '[data-half-shipping-milestone]';

    const existing = container.querySelector(halfSelector);

    if (existing) {
      return existing;
    }

    const sourceSelector = floating
      ? '[data-floating-shipping-milestone]'
      : '[data-shipping-milestone]';

    const source = container.querySelector(sourceSelector);

    if (!source) {
      return null;
    }

    const milestone = source.cloneNode(true);

    milestone.removeAttribute(
      floating
        ? 'data-floating-shipping-milestone'
        : 'data-shipping-milestone'
    );

    milestone.setAttribute(
      floating
        ? 'data-floating-half-shipping-milestone'
        : 'data-half-shipping-milestone',
      ''
    );

    source.parentNode?.insertBefore(milestone, source);

    return milestone;
  }

  function getCopy(element) {
    const data = element?.dataset || {};
    const halfShippingFallbacks =
      getHalfShippingFallbacks();

    return {
      updating: data.copyUpdating || '',

      giftBefore: (limit) =>
        fillTemplate(data.copyGiftBefore, {
          LIMIT: limit,
        }),

      addMore: (amount) =>
        fillTemplate(data.copyAddMore, {
          AMOUNT: amount,
        }),

      giftAdded:
        data.copyGiftAdded || '',

      halfShippingRemaining: (amount) =>
        data.copyHalfShippingRemaining
          ? fillTemplate(
              data.copyHalfShippingRemaining,
              {
                AMOUNT: amount,
              }
            )
          : halfShippingFallbacks.remaining(
              amount
            ),

      halfShippingUnlocked:
        data.copyHalfShippingUnlocked ||
        halfShippingFallbacks.unlocked,

      shippingRemaining: (amount) =>
        fillTemplate(
          data.copyShippingRemaining,
          {
            AMOUNT: amount,
          }
        ),

      allBenefits:
        data.copyAllBenefits || '',

      allActive:
        data.copyAllActive || '',

      shippingOnly:
        data.copyShippingOnly || '',

      shippingOnlyActive:
        data.copyShippingOnlyActive || '',

      popupGiftBefore: (
        limit,
        amount
      ) =>
        fillTemplate(
          data.copyPopupGiftBefore,
          {
            LIMIT: limit,
            AMOUNT: amount,
          }
        ),

      popupGiftAdded: (amount) =>
        fillTemplate(
          data.copyPopupGiftAdded,
          {
            AMOUNT: amount,
          }
        ),

      popupHalfShippingRemaining: (
        amount
      ) =>
        data.copyPopupHalfShippingRemaining
          ? fillTemplate(
              data.copyPopupHalfShippingRemaining,
              {
                AMOUNT: amount,
              }
            )
          : halfShippingFallbacks
              .popupRemaining(amount),

      popupHalfShippingUnlocked: (
        amount
      ) =>
        data.copyPopupHalfShippingUnlocked
          ? fillTemplate(
              data.copyPopupHalfShippingUnlocked,
              {
                AMOUNT: amount,
              }
            )
          : halfShippingFallbacks
              .popupUnlocked(amount),

      popupAllActive:
        data.copyPopupAllActive || '',

      popupShippingOnly:
        data.copyPopupShippingOnly || '',
    };
  }

  function getCurrencyLocale(currency) {
    const language = getLanguage();

    if (!language.startsWith('en')) {
      return language;
    }

    const locales = {
      SEK: 'sv-SE',
      DKK: 'da-DK',
      PLN: 'pl-PL',
      CZK: 'cs-CZ',
      HUF: 'hu-HU',
      RON: 'ro-RO',
    };

    return locales[currency] || language;
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(
        getCurrencyLocale(currency),
        {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      ).format(
        Number(cents || 0) / 100
      );
    } catch (error) {
      return `${(
        Number(cents || 0) / 100
      ).toFixed(2)} ${currency}`;
    }
  }

  function getThresholds(cart) {
    const currency =
      cart.currency ||
      window.Shopify?.currency?.active ||
      'EUR';

    /*
     * .EU
     *
     * Rajat lasketaan Global trust barissa keskitetysti
     * config/settings_schema.json-arvoista ja aktiivisesta
     * Shopify Markets -valuutasta.
     *
     * Tämä tiedosto EI tee omaa EU-valuuttamuunnosta eikä
     * sisällä omia EU-raja-arvoja.
     */
    if (isEuStore) {
      const current =
        window.ACBenefits?.current;

      const gift =
        Number(current?.giftCents);

      const halfShipping =
        Number(
          current?.halfShippingCents
        );

      const shipping =
        Number(
          current?.freeShippingCents
        );

      if (
        !Number.isFinite(gift) ||
        gift <= 0 ||
        !Number.isFinite(halfShipping) ||
        halfShipping <= 0 ||
        !Number.isFinite(shipping) ||
        shipping <= 0
      ) {
        return null;
      }

      return {
        currency,
        gift,
        halfShipping,
        shipping,
      };
    }

    /*
     * .FI pidetään ennallaan.
     */
    return {
      currency,
      gift: FI_THRESHOLDS.gift,
      halfShipping:
        FI_THRESHOLDS.halfShipping,
      shipping:
        FI_THRESHOLDS.shipping,
    };
  }

  function isAutomaticGift(item) {
    return (
      Number(item.variant_id) ===
        GIFT_VARIANT_ID &&
      String(
        item.properties?._auto_gift
      ) === 'true'
    );
  }

  function isGiftCard(item) {
    return item?.gift_card === true;
  }

  function isAdventCalendar(item) {
    return (
      item?.product_type === 'Advent Calendar' ||
      item?.product_type === 'Joulukalenteri'
    );
  }

  /*
   * LAHJAA VARTEN LASKETTAVA SUMMA
   *
   * Joulukalenteri EI kerrytä ilmaiseen
   * kristalliin oikeuttavaa summaa.
   */
  function getGiftQualifyingSubtotal(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (
        isAutomaticGift(item) ||
        isGiftCard(item) ||
        isAdventCalendar(item)
      ) {
        return sum;
      }

      return sum + Number(
        item.final_line_price ??
        item.line_price ??
        item.original_line_price ??
        0
      );
    }, 0);
  }

  /*
   * TOIMITUSETUJA VARTEN LASKETTAVA SUMMA
   *
   * Joulukalenterit lasketaan mukaan
   * toimitusetuihin normaalisti.
   */
  function getShippingQualifyingSubtotal(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (
        isAutomaticGift(item) ||
        isGiftCard(item)
      ) {
        return sum;
      }

      return sum + Number(
        item.final_line_price ??
        item.line_price ??
        item.original_line_price ??
        0
      );
    }, 0);
  }

  async function fetchCart() {
    const response = await fetch(
      `${cartRoot}cart.js`,
      {
        cache: 'no-store',

        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        'Failed to fetch cart'
      );
    }

    return response.json();
  }

  function runWhenReady(callback) {
    if (
      document.readyState === 'loading'
    ) {
      document.addEventListener(
        'DOMContentLoaded',
        callback,
        {
          once: true,
        }
      );
    } else {
      callback();
    }
  }

  class FreeShippingBar {
    constructor() {
      this.lastCart = null;
      this.barObserver = null;

      this.init();
    }

    init() {
      this.observeReRenderedBars();

      runWhenReady(() =>
        this.refresh()
      );

      document.addEventListener(
        'cart:refresh',
        (event) => {
          if (event.detail?.cart) {
            this.updateAllBars(
              event.detail.cart
            );
          } else {
            this.refresh();
          }
        }
      );

      document.addEventListener(
        'ac:benefits-updated',
        () => {
          this.refresh();
        }
      );

      document.addEventListener(
        'change',
        (event) => {
          if (
            event.target.matches(
              'input[name="updates[]"]'
            ) ||
            event.target.matches(
              '.quantity__input'
            )
          ) {
            setTimeout(
              () => this.refresh(),
              400
            );

            setTimeout(
              () => this.refresh(),
              900
            );
          }
        }
      );

      document.addEventListener(
        'click',
        (event) => {
          if (
            event.target.closest(
              'button[name="plus"]'
            ) ||
            event.target.closest(
              'button[name="minus"]'
            ) ||
            event.target.closest(
              'button[type="submit"][name="add"]'
            ) ||
            event.target.closest(
              '[name="add"]'
            ) ||
            event.target.closest(
              'cart-remove-button'
            )
          ) {
            setTimeout(
              () => this.refresh(),
              400
            );

            setTimeout(
              () => this.refresh(),
              1000
            );
          }
        }
      );
    }

    async refresh() {
      try {
        const cart =
          await fetchCart();

        this.updateAllBars(cart);
      } catch (error) {
        console.error(
          '[Cart benefits] Progress bar update failed:',
          error
        );
      }
    }

    updateAllBars(cart) {
      if (!cart) return;

      /*
       * Säilytetään viimeisin ostoskorin tila.
       * Jos Dawn rakentaa palkin uudelleen,
       * sama tila voidaan palauttaa ilman
       * uutta verkkopyyntöä.
       */
      this.lastCart = cart;

      document
        .querySelectorAll(
          '[data-cart-benefits-bar]'
        )
        .forEach((bar) =>
          this.updateBar(
            bar,
            cart
          )
        );
    }

    observeReRenderedBars() {
      if (this.barObserver) {
        return;
      }

      runWhenReady(() => {
        this.barObserver =
          new MutationObserver(
            (mutations) => {
              let newBarAdded = false;

              for (
                const mutation of mutations
              ) {
                for (
                  const addedNode
                  of mutation.addedNodes
                ) {
                  if (
                    !(
                      addedNode instanceof
                      Element
                    )
                  ) {
                    continue;
                  }

                  if (
                    addedNode.matches(
                      '[data-cart-benefits-bar]'
                    ) ||
                    addedNode.querySelector(
                      '[data-cart-benefits-bar]'
                    )
                  ) {
                    newBarAdded = true;
                    break;
                  }
                }

                if (newBarAdded) {
                  break;
                }
              }

              /*
               * Päivitetään uudelleen luotu
               * palkki heti viimeisillä tiedoilla.
               */
              if (
                newBarAdded &&
                this.lastCart
              ) {
                this.updateAllBars(
                  this.lastCart
                );
              }
            }
          );

        this.barObserver.observe(
          document.body,
          {
            childList: true,
            subtree: true,
          }
        );
      });
    }

    updateBar(bar, cart) {
      const copy =
        getCopy(bar);

      const thresholds =
        getThresholds(cart);

      if (!thresholds) {
        return;
      }

      /*
       * Lahjalle ja toimitukselle käytetään
       * eri ostoskorisummaa.
       *
       * giftTotal:
       * Advent Calendar EI mukana.
       *
       * shippingTotal:
       * Advent Calendar ON mukana.
       */
      const giftTotal =
        getGiftQualifyingSubtotal(cart);

      const shippingTotal =
        getShippingQualifyingSubtotal(cart);

      const remainingToGift =
        Math.max(
          thresholds.gift - giftTotal,
          0
        );

      const remainingToHalfShipping =
        thresholds.halfShipping !== null
          ? Math.max(
              thresholds.halfShipping -
                shippingTotal,
              0
            )
          : 0;

      const remainingToShipping =
        Math.max(
          thresholds.shipping -
            shippingTotal,
          0
        );

      /*
       * Progress seuraa toimitukseen
       * oikeuttavaa ostoskorisummaa.
       */
      const progress =
        thresholds.shipping > 0
          ? Math.min(
              (
                shippingTotal /
                thresholds.shipping
              ) * 100,
              100
            )
          : 0;

      const giftPosition =
        thresholds.shipping > 0
          ? (
              thresholds.gift /
              thresholds.shipping
            ) * 100
          : 0;

      const halfShippingPosition =
        thresholds.halfShipping !== null &&
        thresholds.shipping > 0
          ? (
              thresholds.halfShipping /
              thresholds.shipping
            ) * 100
          : null;

      const textEl =
        bar.querySelector(
          '[data-free-shipping-text]'
        );

      const subTextEl =
        bar.querySelector(
          '[data-free-shipping-subtext]'
        );

      const progressEl =
        bar.querySelector(
          '[data-free-shipping-progress]'
        );

      const giftMilestone =
        bar.querySelector(
          '[data-gift-milestone]'
        );

      const halfShippingMilestone =
        ensureHalfShippingMilestone(
          bar,
          false
        );

      const shippingMilestone =
        bar.querySelector(
          '[data-shipping-milestone]'
        );

      /*
       * Progress bar.
       */
      if (progressEl) {
        const isNewProgressElement =
          progressEl.dataset
            .progressInitialized !==
          'true';

        if (
          isNewProgressElement
        ) {
          progressEl.style
            .setProperty(
              'transition',
              'none',
              'important'
            );
        }

        progressEl.style
          .setProperty(
            'width',
            `${progress}%`,
            'important'
          );

        if (
          isNewProgressElement
        ) {
          void progressEl.offsetWidth;

          progressEl.dataset
            .progressInitialized =
            'true';

          requestAnimationFrame(
            () => {
              progressEl.style
                .removeProperty(
                  'transition'
                );
            }
          );
        }
      }

      /*
       * Milestone-paikat.
       */
      if (giftMilestone) {
        giftMilestone.style.left =
          `${giftPosition}%`;
      }

      if (
        halfShippingMilestone &&
        halfShippingPosition !== null
      ) {
        halfShippingMilestone
          .style.left =
          `${halfShippingPosition}%`;
      }

      const giftLabel =
        formatMoney(
          thresholds.gift,
          thresholds.currency
        );

      const halfShippingLabel =
        thresholds.halfShipping !== null
          ? formatMoney(
              thresholds.halfShipping,
              thresholds.currency
            )
          : '';

      const shippingLabel =
        formatMoney(
          thresholds.shipping,
          thresholds.currency
        );

      /*
       * Lahjapiste käyttää AINOASTAAN
       * giftTotalia.
       */
      this.updateMilestone(
        giftMilestone,
        giftTotal >=
          thresholds.gift,
        '🎁',
        giftLabel
      );

      /*
       * Toimituspisteet käyttävät
       * shippingTotalia.
       */
      if (
        halfShippingMilestone
      ) {
        this.updateMilestone(
          halfShippingMilestone,
          shippingTotal >=
            thresholds.halfShipping,
          '🚚',
          halfShippingLabel
        );
      }

      this.updateMilestone(
        shippingMilestone,
        shippingTotal >=
          thresholds.shipping,
        '🚚',
        shippingLabel
      );

      if (!textEl) {
        return;
      }

      textEl.classList.remove(
        'free-shipping-bar__text--success',
        'free-shipping-bar-compact__text--success'
      );

      if (subTextEl) {
        subTextEl.innerHTML = '';
      }

      /*
       * TILA 1:
       *
       * ILMAINEN TOIMITUS SAAVUTETTU.
       *
       * Tarkistetaan tämä ensin, koska
       * esimerkiksi Advent Calendar voi
       * saavuttaa toimitusrajan ilman,
       * että se oikeuttaa ilmaiseen lahjaan.
       */
      if (
        shippingTotal >=
        thresholds.shipping
      ) {
        if (
          textEl.classList.contains(
            'free-shipping-bar-compact__text'
          )
        ) {
          textEl.classList.add(
            'free-shipping-bar-compact__text--success'
          );
        } else {
          textEl.classList.add(
            'free-shipping-bar__text--success'
          );
        }

        /*
         * Molemmat edut saavutettu.
         */
        if (
          giftTotal >=
          thresholds.gift
        ) {
          textEl.innerHTML =
            copy.allBenefits;

          if (subTextEl) {
            subTextEl.innerHTML =
              copy.allActive;
          }
        }

        /*
         * Vain ilmainen toimitus saavutettu.
         *
         * Esimerkiksi joulukalenteri yksin.
         */
        else {
          textEl.innerHTML =
            copy.shippingOnly;

          if (subTextEl) {
            subTextEl.innerHTML =
              copy.shippingOnlyActive;
          }
        }
      }

      /*
       * TILA 2:
       *
       * Ilmaiseen lahjaan oikeuttavaa
       * ostoskorisummaa ei ole vielä
       * tarpeeksi.
       */
      else if (
        giftTotal <
        thresholds.gift
      ) {
        textEl.innerHTML =
          copy.giftBefore(
            giftLabel
          );

        if (subTextEl) {
          subTextEl.innerHTML =
            copy.addMore(
              formatMoney(
                remainingToGift,
                thresholds.currency
              )
            );
        }
      }

      /*
       * TILA 3 EU:
       *
       * Ilmainen lahja saavutettu,
       * mutta puolitetun toimituksen etua
       * ei vielä saavutettu.
       */
      else if (
        isEuStore &&
        thresholds.halfShipping !==
          null &&
        shippingTotal <
          thresholds.halfShipping
      ) {
        textEl.innerHTML =
          copy.giftAdded;

        if (subTextEl) {
          subTextEl.innerHTML =
            copy.halfShippingRemaining(
              formatMoney(
                remainingToHalfShipping,
                thresholds.currency
              )
            );
        }
      }

      /*
       * TILA 4:
       *
       * Lahja saavutettu ja ollaan
       * matkalla ilmaiseen toimitukseen.
       */
      else {
        textEl.innerHTML =
          isEuStore &&
          thresholds.halfShipping !==
            null &&
          shippingTotal >=
            thresholds.halfShipping
            ? copy
                .halfShippingUnlocked
            : copy.giftAdded;

        if (subTextEl) {
          subTextEl.innerHTML =
            copy.shippingRemaining(
              formatMoney(
                remainingToShipping,
                thresholds.currency
              )
            );
        }
      }

      bar.classList.add(
        'is-ready'
      );
    }

    updateMilestone(
      milestone,
      active,
      inactiveIcon,
      labelText
    ) {
      if (!milestone) {
        return;
      }

      milestone.classList.toggle(
        'is-active',
        active
      );

      const dot =
        milestone.querySelector(
          '.free-shipping-bar__milestone-dot'
        ) ||
        milestone.querySelector(
          '.free-shipping-bar-compact__milestone-dot'
        );

      const label =
        milestone.querySelector(
          '.free-shipping-bar__milestone-label'
        ) ||
        milestone.querySelector(
          '.free-shipping-bar-compact__milestone-label'
        );

      if (dot) {
        dot.textContent =
          active
            ? '✓'
            : inactiveIcon;
      }

      if (label) {
        label.textContent =
          labelText;
      }
    }
  }

  class FloatingFreeShippingPopup {
    constructor() {
      this.popup =
        document.querySelector(
          '[data-floating-free-shipping-popup]'
        );

      if (!this.popup) {
        return;
      }

      this.textEl =
        this.popup.querySelector(
          '[data-floating-free-shipping-text]'
        );

      this.progressEl =
        this.popup.querySelector(
          '[data-floating-free-shipping-progress]'
        );

      this.giftMilestone =
        this.popup.querySelector(
          '[data-floating-gift-milestone]'
        );

      this.halfShippingMilestone =
        ensureHalfShippingMilestone(
          this.popup,
          true
        );

      this.shippingMilestone =
        this.popup.querySelector(
          '[data-floating-shipping-milestone]'
        );

      this.previousItemCount =
        null;

      this.hideTimeout =
        null;

      this.init();
    }

    init() {
      runWhenReady(() =>
        this.refresh(true)
      );

      document.addEventListener(
        'cart:refresh',
        (event) => {
          if (event.detail?.cart) {
            this.update(
              event.detail.cart
            );
          } else {
            this.refresh();
          }
        }
      );

      document.addEventListener(
        'ac:benefits-updated',
        () => {
          this.refresh();
        }
      );

      document.addEventListener(
        'click',
        (event) => {
          if (
            event.target.closest(
              'button[type="submit"][name="add"]'
            ) ||
            event.target.closest(
              '[name="add"]'
            )
          ) {
            setTimeout(
              () => this.refresh(),
              500
            );

            setTimeout(
              () => this.refresh(),
              1100
            );
          }
        }
      );
    }

    async refresh(
      initial = false
    ) {
      if (
        window.innerWidth >= 750
      ) {
        return;
      }

      try {
        const cart =
          await fetchCart();

        this.update(
          cart,
          initial
        );
      } catch (error) {
        console.error(
          '[Cart benefits] Floating popup update failed:',
          error
        );
      }
    }

    update(
      cart,
      initial = false
    ) {
      if (
        window.innerWidth >= 750
      ) {
        return;
      }

      const copy =
        getCopy(this.popup);

      const thresholds =
        getThresholds(cart);

      if (!thresholds) {
        return;
      }

      const giftTotal =
        getGiftQualifyingSubtotal(cart);

      const shippingTotal =
        getShippingQualifyingSubtotal(cart);

      const itemCount =
        Number(
          cart.item_count || 0
        );

      const remainingToGift =
        Math.max(
          thresholds.gift -
            giftTotal,
          0
        );

      const remainingToHalfShipping =
        thresholds.halfShipping !==
          null
          ? Math.max(
              thresholds
                .halfShipping -
                shippingTotal,
              0
            )
          : 0;

      const remainingToShipping =
        Math.max(
          thresholds.shipping -
            shippingTotal,
          0
        );

      const progress =
        thresholds.shipping > 0
          ? Math.min(
              (
                shippingTotal /
                thresholds.shipping
              ) * 100,
              100
            )
          : 0;

      const giftPosition =
        thresholds.shipping > 0
          ? (
              thresholds.gift /
              thresholds.shipping
            ) * 100
          : 0;

      const halfShippingPosition =
        thresholds.halfShipping !==
          null &&
        thresholds.shipping > 0
          ? (
              thresholds
                .halfShipping /
              thresholds.shipping
            ) * 100
          : null;

      if (this.progressEl) {
        this.progressEl.style.width =
          `${progress}%`;
      }

      if (
        this.giftMilestone
      ) {
        this.giftMilestone
          .style.left =
          `${giftPosition}%`;
      }

      if (
        this.halfShippingMilestone &&
        halfShippingPosition !== null
      ) {
        this.halfShippingMilestone
          .style.left =
          `${halfShippingPosition}%`;
      }

      const giftLabel =
        formatMoney(
          thresholds.gift,
          thresholds.currency
        );

      const halfShippingLabel =
        thresholds.halfShipping !==
          null
          ? formatMoney(
              thresholds
                .halfShipping,
              thresholds.currency
            )
          : '';

      const shippingLabel =
        formatMoney(
          thresholds.shipping,
          thresholds.currency
        );

      /*
       * Lahjapiste käyttää giftTotalia.
       */
      this.updateMilestone(
        this.giftMilestone,
        giftTotal >=
          thresholds.gift,
        '🎁',
        giftLabel
      );

      /*
       * Toimituspisteet käyttävät
       * shippingTotalia.
       */
      if (
        this.halfShippingMilestone
      ) {
        this.updateMilestone(
          this.halfShippingMilestone,
          shippingTotal >=
            thresholds
              .halfShipping,
          '🚚',
          halfShippingLabel
        );
      }

      this.updateMilestone(
        this.shippingMilestone,
        shippingTotal >=
          thresholds.shipping,
        '🚚',
        shippingLabel
      );

      if (this.textEl) {
        /*
         * Ilmainen toimitus saavutettu.
         */
        if (
          shippingTotal >=
          thresholds.shipping
        ) {
          /*
           * Sekä lahja että toimitus.
           */
          if (
            giftTotal >=
            thresholds.gift
          ) {
            this.textEl.innerHTML =
              copy.popupAllActive;
          }

          /*
           * Vain ilmainen toimitus.
           */
          else {
            this.textEl.innerHTML =
              copy.popupShippingOnly;
          }
        }

        /*
         * Lahjaan oikeuttavaa summaa
         * ei vielä ole tarpeeksi.
         */
        else if (
          giftTotal <
          thresholds.gift
        ) {
          this.textEl.innerHTML =
            copy.popupGiftBefore(
              giftLabel,
              formatMoney(
                remainingToGift,
                thresholds.currency
              )
            );
        }

        /*
         * EU:
         * Lahja saavutettu, mutta puolitetun toimituksen
         * etua ei vielä saavutettu.
         */
        else if (
          isEuStore &&
          thresholds.halfShipping !==
            null &&
          shippingTotal <
            thresholds.halfShipping
        ) {
          this.textEl.innerHTML =
            copy
              .popupHalfShippingRemaining(
                formatMoney(
                  remainingToHalfShipping,
                  thresholds.currency
                )
              );
        }

        /*
         * Kohti ilmaista toimitusta.
         */
        else {
          if (
            isEuStore &&
            thresholds.halfShipping !==
              null &&
            shippingTotal >=
              thresholds.halfShipping
          ) {
            this.textEl.innerHTML =
              copy
                .popupHalfShippingUnlocked(
                  formatMoney(
                    remainingToShipping,
                    thresholds.currency
                  )
                );
          } else {
            this.textEl.innerHTML =
              copy.popupGiftAdded(
                formatMoney(
                  remainingToShipping,
                  thresholds.currency
                )
              );
          }
        }
      }

      if (initial) {
        this.previousItemCount =
          itemCount;

        this.hide(true);

        return;
      }

      const countIncreased =
        this.previousItemCount !==
          null &&
        itemCount >
          this.previousItemCount;

      if (countIncreased) {
        this.showTemporarily();
      }

      if (itemCount === 0) {
        this.hide(true);
      }

      this.previousItemCount =
        itemCount;
    }

    updateMilestone(
      milestone,
      active,
      inactiveIcon,
      labelText
    ) {
      if (!milestone) {
        return;
      }

      milestone.classList.toggle(
        'is-active',
        active
      );

      const dot =
        milestone.querySelector(
          '.floating-free-shipping-popup__milestone-dot'
        );

      const label =
        milestone.querySelector(
          '.floating-free-shipping-popup__milestone-label'
        );

      if (dot) {
        dot.textContent =
          active
            ? '✓'
            : inactiveIcon;
      }

      if (label) {
        label.textContent =
          labelText;
      }
    }

    showTemporarily() {
      clearTimeout(
        this.hideTimeout
      );

      this.popup.classList.remove(
        'hidden'
      );

      this.popup.setAttribute(
        'aria-hidden',
        'false'
      );

      requestAnimationFrame(
        () => {
          this.popup.classList.add(
            'is-visible'
          );
        }
      );

      this.hideTimeout =
        setTimeout(
          () => {
            this.hide();
          },
          3000
        );
    }

    hide(force = false) {
      clearTimeout(
        this.hideTimeout
      );

      this.popup.classList.remove(
        'is-visible'
      );

      this.popup.setAttribute(
        'aria-hidden',
        'true'
      );

      if (force) {
        this.popup.classList.add(
          'hidden'
        );

        return;
      }

      setTimeout(
        () => {
          if (
            !this.popup
              .classList
              .contains(
                'is-visible'
              )
          ) {
            this.popup.classList.add(
              'hidden'
            );
          }
        },
        250
      );
    }
  }

  /*
   * Automaattinen ilmainen kristalli.
   *
   * .FI 25 €
   * .EU: raja tulee window.ACBenefits.current-objektista
   *
   * Lahjakortti ja Advent Calendar
   * eivät oikeuta ilmaiseen lahjaan.
   */
  class GiftWithPurchase {
    constructor() {
      this.giftVariantId =
        GIFT_VARIANT_ID;

      this.isUpdating =
        false;

      this.scheduledSync =
        null;

      this.sectionIds = [
        'main-cart-items',
        'cart-icon-bubble',
      ];

      this.init();
    }

    init() {
      runWhenReady(() =>
        this.syncGift()
      );

      document.addEventListener(
        'cart:refresh',
        (event) => {
          if (
            event.detail?.source ===
            'automatic-gift'
          ) {
            return;
          }

          this.scheduleSync(100);
        }
      );

      document.addEventListener(
        'ac:benefits-updated',
        () => {
          this.scheduleSync(0);
        }
      );

      document.addEventListener(
        'change',
        (event) => {
          if (
            event.target.matches(
              'input[name="updates[]"]'
            ) ||
            event.target.matches(
              '.quantity__input'
            )
          ) {
            this.scheduleSync(
              700
            );
          }
        }
      );

      document.addEventListener(
        'click',
        (event) => {
          if (
            event.target.closest(
              'button[name="plus"]'
            ) ||
            event.target.closest(
              'button[name="minus"]'
            ) ||
            event.target.closest(
              'cart-remove-button'
            ) ||
            event.target.closest(
              'button[type="submit"][name="add"]'
            ) ||
            event.target.closest(
              '[name="add"]'
            )
          ) {
            this.scheduleSync(
              900
            );
          }
        }
      );
    }

    scheduleSync(delay) {
      clearTimeout(
        this.scheduledSync
      );

      this.scheduledSync =
        setTimeout(
          () => {
            this.syncGift();
          },
          delay
        );
    }

    async syncGift() {
      if (this.isUpdating) {
        return;
      }

      try {
        const cart =
          await fetchCart();

        const giftLines =
          this.findGiftLines(
            cart
          );

        const giftLine =
          giftLines[0] ||
          null;

        const thresholds =
          getThresholds(cart);

        if (!thresholds) {
          return;
        }

        /*
         * Ilmaislahjan laskenta käyttää
         * vain giftTotal-logiikkaa.
         *
         * Advent Calendar ei siis
         * laukaise ilmaista kristallia.
         */
        const subtotal =
          getGiftQualifyingSubtotal(
            cart
          );

        const qualifies =
          subtotal >=
          thresholds.gift;

        if (
          qualifies &&
          !giftLine
        ) {
          await this.addGift();
          return;
        }

        if (
          !qualifies &&
          giftLines.length
        ) {
          await this
            .removeAllGiftLines(
              giftLines
            );

          return;
        }

        if (
          giftLine &&
          giftLine.quantity !== 1
        ) {
          await this
            .setGiftQuantity(
              giftLine.key,
              1
            );

          return;
        }

        if (
          giftLines.length > 1
        ) {
          await this
            .removeDuplicateGiftLines(
              giftLines.slice(1)
            );
        }
      } catch (error) {
        console.error(
          '[Automatic gift] Synchronization failed:',
          error
        );
      }
    }

    findGiftLines(cart) {
      return (
        cart.items || []
      ).filter((item) =>
        isAutomaticGift(item)
      );
    }

    getSectionRequestData() {
      return {
        sections:
          this.sectionIds,

        sections_url:
          window.location.pathname,
      };
    }

    async addGift() {
      this.isUpdating =
        true;

      try {
        const response =
          await fetch(
            `${cartRoot}cart/add.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',
              },

              body:
                JSON.stringify({
                  items: [
                    {
                      id:
                        this
                          .giftVariantId,

                      quantity: 1,

                      properties: {
                        _auto_gift:
                          'true',
                      },
                    },
                  ],

                  ...this
                    .getSectionRequestData(),
                }),
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.description ||
              'Failed to add gift'
          );
        }

        this.renderSections(
          data.sections
        );

        await this
          .afterGiftChange();
      } finally {
        this.isUpdating =
          false;
      }
    }

    async removeAllGiftLines(
      giftLines
    ) {
      this.isUpdating =
        true;

      try {
        const updates = {};

        giftLines.forEach(
          (line) => {
            updates[line.key] =
              0;
          }
        );

        const response =
          await fetch(
            `${cartRoot}cart/update.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',
              },

              body:
                JSON.stringify({
                  updates,

                  ...this
                    .getSectionRequestData(),
                }),
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.description ||
              'Failed to remove gift'
          );
        }

        this.renderSections(
          data.sections
        );

        await this
          .afterGiftChange();
      } finally {
        this.isUpdating =
          false;
      }
    }

    async removeDuplicateGiftLines(
      giftLines
    ) {
      return this
        .removeAllGiftLines(
          giftLines
        );
    }

    async setGiftQuantity(
      lineKey,
      quantity
    ) {
      this.isUpdating =
        true;

      try {
        const response =
          await fetch(
            `${cartRoot}cart/change.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',
              },

              body:
                JSON.stringify({
                  id: lineKey,

                  quantity,

                  ...this
                    .getSectionRequestData(),
                }),
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data.description ||
              'Failed to update gift quantity'
          );
        }

        this.renderSections(
          data.sections
        );

        await this
          .afterGiftChange();
      } finally {
        this.isUpdating =
          false;
      }
    }

    renderSections(
      sections
    ) {
      if (!sections) {
        return;
      }

      /*
       * Päivitetään vain ostoskorin tuotteet
       * ja korikuvake.
       *
       * main-cart-footer jätetään Dawnin
       * hallintaan, jotta loppusumma ja
       * checkout-painikkeet eivät häviä.
       */
      if (
        sections[
          'main-cart-items'
        ]
      ) {
        const parsedItems =
          new DOMParser()
            .parseFromString(
              sections[
                'main-cart-items'
              ],
              'text/html'
            );

        const sourceItems =
          parsedItems.querySelector(
            '#main-cart-items .js-contents'
          );

        const targetItems =
          document.querySelector(
            '#main-cart-items .js-contents'
          );

        if (
          sourceItems &&
          targetItems
        ) {
          targetItems.innerHTML =
            sourceItems.innerHTML;
        }
      }

      if (
        sections[
          'cart-icon-bubble'
        ]
      ) {
        const parsedBubble =
          new DOMParser()
            .parseFromString(
              sections[
                'cart-icon-bubble'
              ],
              'text/html'
            );

        const sourceBubble =
          parsedBubble.querySelector(
            '#cart-icon-bubble'
          );

        const targetBubble =
          document.querySelector(
            '#cart-icon-bubble'
          );

        if (
          sourceBubble &&
          targetBubble
        ) {
          targetBubble.innerHTML =
            sourceBubble.innerHTML;
        }
      }
    }

    async afterGiftChange() {
      const cart =
        await fetchCart();

      document.dispatchEvent(
        new CustomEvent(
          'cart:refresh',
          {
            detail: {
              cart,
              source:
                'automatic-gift',
            },
          }
        )
      );
    }
  }

  window.freeShippingBar =
    new FreeShippingBar();

  runWhenReady(() => {
    window.floatingFreeShippingPopup =
      new FloatingFreeShippingPopup();

    window.giftWithPurchase =
      new GiftWithPurchase();
  });
})();